<template>
  <div ref="addFeeTaxesDialogContainer">
    <ed-modal
      :show="show"
      :title="title"
      primary-button="Save"
      secondary-button="Cancel"
      type="medium"
      @close="onClose"
      @validate="onSave"
      :validation="validation"
      :closeOnEsc="true"
      :submitEndpoint="computePoTotalEndpoint"
      name='add_line_item'
    >
      <div slot="body">
        <section class="col-md-10 no-pad">
          <h5 class="section-title">Item Details</h5>
          <ed-input
            v-model="formData.draftItem.name"
            field-label="Product Name"
            required="required"
            :disabled="model.canEditOnlyPrice"
            @change='itemNameChanged'
            :error="doDisplayNameError"
            helptip-header=""
            :helptip-body="doDisplayNameError && 'Please fill in a name' || ''"
            name='product_name'
            ref="productNameField"
          />

          <ed-textarea
            v-model="formData.draftItem.description"
            :disabled="model.canEditOnlyPrice"
            field-label="Description"
            name='description'
          />

          <ed-textarea v-model="formData.draftItem.internalNote"
                       :disabled="model.canEditOnlyPrice"
                       field-label="Internal Note (not visible to supplier)"
                       name='internalNote' />

          <ed-select
            v-if="categories"
            field-label="Category"
            placeholder="Select a Category"
            :options="categories"
            v-model="formData.draftItem.selectedCategory"
            required="required"
            :disabled="model.canEditOnlyPrice"
            label="text"
            track-by="key"
            @select="categoryChanged"
            :error="doDisplayCategoryError"
            helptip-header=""
            :helptip-body="doDisplayCategoryError && 'Please select a category' || ''"
            name='category'
          />

          <ed-input
            field-label="Category Ref ID"
            v-model="formData.draftItem.referenceId"
            :disabled='disableCategoryRefInput'
            @change='categoryRefIdChanged'
            :required='isRequiredCategRefID'
            maxlength="50"
            :error="isRequiredCategRefID && doDisplayCategRefIdErrorComputed"
            helptip-header=""
            :helptip-body="isRequiredCategRefID && doDisplayCategRefIdErrorComputed && 'Please fill in a Category Ref ID' || ''"
            name='category_ref_id'
          />

          <div class="row" v-if="showOneBusinessUnit">
            <label class="col-md-4 no-pad">Business Unit</label>
            <span class="col-md-8 small" name='one_bu_name'>{{businessUnit.name}}</span>
          </div>

          <div class="row" v-if="showBusinessUnits">
            <ed-select
              field-label="Business Unit"
              placeholder="Select business unit"
              :options="businessUnits"
              required="required"
              :disabled="model.canEditOnlyPrice"
              label="name"
              track-by="key"
              v-model="formData.draftItem.businessUnit"
              @select="businessUnitSelected($event)"
              :error="doDisplayBusinessUnitError"
              helptip-header=""
              :helptip-body="doDisplayBusinessUnitError && 'Please select a Business Unit' || ''"
              name='business_units'
            />
          </div>

          <div class="row" v-if="showOneCostCenter">
            <label class="col-md-4 no-pad">Cost Center</label>
            <span class="col-md-8 small" name='one_cc_name'> {{this.getCostCenterName(costCenter)}} </span>
          </div>

          <div class="row" v-if="showCostCenters">
            <ed-select
              field-label="Cost Center"
              placeholder="Select a Cost Center"
              :options="costCenters"
              required="required"
              :disabled="model.canEditOnlyPrice"
              label="name"
              track-by="key"
              :customLabel='getCostCenterName'
              v-model="formData.draftItem.costCenter"
              @select="costCenterSelected($event)"
              :error="doDisplayCostCenterError"
              helptip-header=""
              :helptip-body="doDisplayCostCenterError && 'Please select a Cost Center' || ''"
              name='cost_centers'
            />
          </div>

          <div class="row" v-if="showOneCostCenterWithBusinessUnit">
            <label class="col-md-4 no-pad">Business Unit and Cost Center</label>
            <span class="col-md-8 small" name='one_bu_cc_pair_name'>{{businessUnitsWithCostCenter.name}}</span>
          </div>

          <div class="row" v-if="showMixCostCentersWithBusinessUnits">
            <ed-select
              field-label="Business Unit and Cost Center"
              placeholder="Select business unit and cost center pair"
              :options="businessUnitsWithCostCenters"
              required="required"
              :disabled="model.canEditOnlyPrice"
              label="name"
              track-by="key"
              v-model="formData.draftItem.pairedBusinessUnitAndCostCenter"
              @select="businessUnitWithCostCenterSelected($event)"
              :error="doDisplayMixCostCenterWithBusinessUnitError"
              helptip-header=""
              :helptip-body="doDisplayMixCostCenterWithBusinessUnitError && 'Please select a Business Unit with Cost Center' || ''"
              name='bu_cc_pairs'
            />
          </div>

          <div class="form-group"></div>

          <ed-select
            v-if="model.units"
            field-label="Unit"
            placeholder="Select an Unit"
            :options="model.units"
            v-model="formData.draftItem.unitTypeModel"
            required="required"
            :disabled="model.canEditOnlyPrice"
            label="text"
            track-by="key"
            @select="unitsChanged"
            :error="doDisplayUnitsError"
            helptip-header=""
            :helptip-body="doDisplayUnitsError && 'Please select a unit' || ''"
            name='select_unit'
          />
		  
		  <ed-input
            v-if="hasSyncSuppliersToAccountingSystem"
            field-label="Tax Code"
            v-model="formData.draftItem.taxCode"
            :disabled='model.canEditOnlyPrice'
            name='taxCode'
          />

          <custom-fields
            :endpoint="api.$.invoice.i2p.itemCustomFields"
            :queryParameters="{invoiceId: model.PaymentInvoiceId, divisionId: model.DivisionID}"
            :values="formData.draftItem.customFields"
            ref="itemCustomFieldsRef">
          </custom-fields>
        </section>
        <section class="col-md-12 no-pad">

          <hr />

          <div class="form-group">
            <div class="row">
              <div class="col-xs-12">
                <h5 class="section-title">Pricing</h5>
                <em class="small">Use the fields below to edit the unit and pricing details for this item(s).</em>
              </div>
            </div>
            <p></p>
            <div
              v-if="model.useSavingsFields === false"
              class="row ghost"
              :endpoint="computeItemTotalEndpoint">
              <div class="col-xs-11 col-md-2">
                <ed-input
                  @change="computeItemTotal"
                  @input="unitPriceInputChanged"
                  field-label="Unit Price"
                  right-label
                  v-model="formData.draftItem.unitPrice"
                  amount
                  autoselect
                  type="money"
                  :currency="currency"
                  :supplementalPrecision='supplementalPrecision'
                  name='unit_price'
                  hide-optional
                  :error="doDisplayUnitPriceError"
                  :helptip-header="''"
                >
                  <span slot="message" v-if="doDisplayUnitPriceError && unitPriceErrorMsg || ''">
                    <p>Unit price must be less than or equal to
                      <v-money class="bold-text" :currency="currency">
                        {{ Number(this.model.maxThreshold.unitPrice) }}
                      </v-money>
                    </p>
                  </span>
                  <span slot="message" v-if="doDisplayUnitPriceError && unitPriceNonZeroMsg || ''">
                    <p>{{ unitPriceNonZeroMsg }}</p>
                  </span>
                </ed-input>
              </div>
              <div class="pull-left text-center">x</div>
              <div class="col-xs-11 col-md-2">
                <ed-input
                  @change="computeItemTotal"
                  @input="quantityChanged"
                  field-label="Quantity"
                  right-label
                  required="required"
                  :error="doDisplayQuantityError"
                  :helptip-header="doDisplayQuantityError &&  'Error' || ''"
                  :helptip-body="doDisplayQuantityError && quantityErrorMsg || ''"
                  amount
                  autoselect
                  v-model="formData.draftItem.quantity"
                  name='quantity'
                  hide-optional
                />
              </div>
              <div class="pull-left text-center align-bottom">x</div>
              <div class="col-xs-11 col-md-2">
                <ed-input
                  @change="computeItemTotal"
                  @input="unitsInputChanged"
                  field-label="Units"
                  right-label
                  amount
                  autoselect
                  v-model="formData.draftItem.units"
                  name='units'
                  hide-optional
                  :error="doDisplayUnitsInputError"
                  :helptip-header="doDisplayUnitsInputError &&  'Error' || ''"
                  :helptip-body="doDisplayUnitsInputError && unitsErrorMsg || ''"
                />
              </div>
              <div class="pull-left">=</div>
              <div class="col-xs-11 col-md-2">
                <ed-input
                  field-label="Total"
                  right-label
                  v-model.number="formData.draftItem.total"
                  amount
                  type="money"
                  :currency="currency"
                  :disabled="true"
                  name='totals'
                  hide-optional
                />
              </div>
            </div>
            <!-- use savings fields -->
            <div v-else>
              <div class="row">
                <div class="col-xs-12 col-md-2">
                  <label>&nbsp;</label>
                </div>
                <div class="heading right-label col-xs-12 col-md-2">
                  <label>Unit Price</label>
                </div>
                <div class="pull-left text-center">&nbsp;&nbsp;</div>
                <div class="heading right-label col-xs-12 col-md-2">
                  <label>Quantity</label>
                </div>
                <div class="pull-left text-center">&nbsp;&nbsp;</div>
                <div class="heading right-label col-xs-12 col-md-2">
                  <label>Units</label>
                </div>
                <div class="pull-left text-center">&nbsp;&nbsp;</div>
                <div class="heading right-label col-xs-12 col-md-3">
                  <label>Total</label>
                </div>
              </div>

              <!-- SGR -->
              <div class="row">
                <div class="col-xs-12 col-md-2">
                  <label>SGR</label>
                </div>
                <div class="col-xs-12 col-md-2">
                  <ed-input
                    @change="sgrChanged"
                    v-model="formData.draftItem.unitPriceSGR"
                    amount
                    autoselect
                    type="money"
                    :supplementalPrecision='supplementalPrecision'
                    :currency="currency"
                    :placeholder="formData.draftItem.unitPricePlaceHolder"
                  ></ed-input>
                </div>
                <div class="pull-left text-center">x</div>
                <div class="col-xs-12 col-md-2">
                  <ed-input
                    @change="computeItemTotal"
                    right-label
                    amount
                    autoselect
                    v-model="formData.draftItem.quantity"
                  ></ed-input>
                </div>
                <div class="pull-left text-center">x</div>
                <div class="col-xs-12 col-md-2">
                  <ed-input
                    @change="computeItemTotal"
                    right-label
                    amount
                    autoselect
                    v-model="formData.draftItem.units"
                  ></ed-input>
                </div>
                <div class="pull-left text-center">=</div>
                <div class="col-xs-12 col-md-3">
                  <ed-input
                    v-model.number="formData.draftItem.totalSGR"
                    amount
                    type="money"
                    :currency="currency"
                    :disabled="true"
                    name='sgr_total'
                  ></ed-input>
                </div>
              </div>

              <!-- PREFERRED -->
              <div class="row">
                <div class="col-xs-12 col-md-2">
                  <label>Preferred Rate</label>
                </div>
                <div class="col-xs-12 col-md-2">
                  <ed-input
                    @change="prefferedRateChanged"
                    v-model="formData.draftItem.unitPricePreferredRate"
                    amount
                    autoselect
                    type="money"
                    :supplementalPrecision='supplementalPrecision'
                    :currency="currency"
                    :placeholder="formData.draftItem.unitPricePlaceHolder"
                  ></ed-input>
                </div>
                <div class="pull-left text-center">x</div>
                <div class="col-xs-12 col-md-2">
                  <ed-input
                    v-model="formData.draftItem.quantity"
                    amount
                    right-label
                    :disabled="true"
                  ></ed-input>
                </div>
                <div class="pull-left text-center">x</div>
                <div class="col-xs-12 col-md-2">
                  <ed-input
                    v-model="formData.draftItem.units"
                    amount
                    right-label
                    :disabled="true"
                  ></ed-input>
                </div>
                <div class="pull-left text-center">=</div>
                <div class="col-xs-12 col-md-3">
                  <ed-input
                    v-model.number="formData.draftItem.totalPreferredRate"
                    amount
                    type="money"
                    :currency="currency"
                    :disabled="true"
                    name='preffered_rate_total'
                  ></ed-input>
                </div>
              </div>

              <!-- NEGOTIATED -->
              <div class="row">
                <div class="col-xs-12 col-md-2">
                  <label>Negotiated Rate</label>
                </div>
                <div class="col-xs-12 col-md-2">
                  <ed-input
                    @change="negotiatedRateChanged"
                    amount
                    autoselect
                    type="money"
                    :currency="currency"
                    :supplementalPrecision='supplementalPrecision'
                    v-model="formData.draftItem.unitPriceNegotiatedRate"
                    :placeholder="formData.draftItem.unitPricePlaceHolder"
                  ></ed-input>
                </div>
                <div class="pull-left text-center">x</div>
                <div class="col-xs-12 col-md-2">
                  <ed-input
                    v-model="formData.draftItem.quantity"
                    amount
                    right-label
                    :disabled="true"
                  ></ed-input>
                </div>
                <div class="pull-left text-center">x</div>
                <div class="col-xs-12 col-md-2">
                  <ed-input
                    v-model="formData.draftItem.units"
                    amount
                    right-label
                    :disabled="true"
                  ></ed-input>
                </div>
                <div class="pull-left text-center">=</div>
                <div class="col-xs-12 col-md-3">
                  <ed-input
                    v-model.number="formData.draftItem.totalNegotiatedRate"
                    amount
                    type="money"
                    :currency="currency"
                    :disabled="true"
                    name='negotiated_rate_total'
                  ></ed-input>
                </div>
              </div>

              <!-- PRICE -->
              <div class="row">
                <div class="col-xs-12 col-md-2">
                  <label>Price</label>
                </div>
                <div class="col-xs-12 col-md-2">
                  <ed-input
                    @change="unitPriceChanged"
                    v-model="formData.draftItem.unitPrice"
                    amount
                    autoselect
                    type="money"
                    :supplementalPrecision='supplementalPrecision'
                    :currency="currency"
                    :error="doDisplayUnitPriceError"
                    :placeholder="formData.draftItem.unitPricePlaceHolder"
                    :helptip-header="''"
                  >
                    <span slot="message" v-if="doDisplayUnitPriceError && unitPriceNonZeroMsg || ''">
                      <p>{{ unitPriceNonZeroMsg }}</p>
                    </span>
                  </ed-input>
                </div>
                <div class="pull-left text-center">x</div>
                <div class="col-xs-12 col-md-2">
                  <ed-input
                    v-model="formData.draftItem.quantity"
                    amount
                    right-label
                    :disabled="true"
                  ></ed-input>
                </div>
                <div class="pull-left text-center">x</div>
                <div class="col-xs-12 col-md-2">
                  <ed-input
                    v-model="formData.draftItem.units"
                    amount
                    right-label
                    :disabled="true"
                  ></ed-input>
                </div>
                <div class="pull-left text-center">=</div>
                <div class="col-xs-12 col-md-3">
                  <ed-input
                    v-model.number="formData.draftItem.total"
                    amount
                    type="money"
                    :currency="currency"
                    :disabled="true"
                    name='price_total'
                  ></ed-input>
                </div>
              </div>

            </div>
          </div>
        </section>

        <section class="col-md-12 no-pad">
          <a class="section-title" href="#" @click.prevent="showDiscountsFeesTaxes = !showDiscountsFeesTaxes">
            <i :class="`icon fas fa-chevron-${showDiscountsFeesTaxes ? 'up' : 'down'}`"></i>
            &nbsp;
            <span>Discounts, Fees and Taxes</span>
          </a>

          <p>&nbsp;</p>
          <div v-if="showDiscountsFeesTaxes" class="form-group">
            <section class="pad">
              <div class="row">
                <div class="heading col-md-4 no-pad"><label> Other</label></div>
                <div class="pull-left text-center">&nbsp;&nbsp;</div>
                <div class="heading right-label col-md-2"><label> Percent</label></div>
                <div class="pull-left text-center">&nbsp;&nbsp;&nbsp;&nbsp;</div>
                <div class="heading right-label col-md-2"><label> Amount</label></div>
              </div>

              <tax-line
                discount
                :model="formData.draftItem"
                :compute="computeItemTotal"
                :context="vm"
                class="row"
              />

              <div class="row">
                <div class="pull-left text-center">&nbsp;&nbsp;</div>
                <div class="col-xs-8 text-right" name="discount_subtotal">
                  <strong>
                    <small>
                      Subtotal: &nbsp;
                      <v-money :currency="currency">{{ formData.draftItem.subtotal }}</v-money>
                    </small>
                  </strong>
                </div>
              </div>
            </section>

            <p>&nbsp;</p>

            <section class="pad">
              <div class="row">
                <div class="heading col-md-4 no-pad" name='taxesLabel'><label> Taxes</label></div>
                <div class="pull-left text-center">&nbsp;&nbsp;</div>
                <div class="heading right-label col-md-2"><label> Percent</label></div>
                <div class="pull-left text-center">&nbsp;&nbsp;&nbsp;&nbsp;</div>
                <div class="heading right-label col-md-2"><label> Amount</label></div>
              </div>

              <tax-line v-for="tax in formData.draftItem.taxes"
                :key="tax.name"
                :model="tax"
                :compute="computeItemTotal"
                :context="vm"
                class="row"
              />

              <div class="row">
                <div class="pull-left text-center">&nbsp;&nbsp;</div>
                <div class="col-xs-8 text-right" name="taxes_subtotal">
                  <strong>
                    <small>
                      Subtotal: &nbsp;
                      <v-money :currency="currency" :supplementalPrecision="6">{{ formData.draftItem.totalTaxes - formData.draftItem.totalTaxesForFees || 0 }}</v-money>
                    </small>
                  </strong>
                </div>
              </div>
            </section>

            <p>&nbsp;</p>

            <section class="pad">
              <div class="row">
                <div class="heading col-md-4 no-pad" name='feesLabel'><label> Fees</label></div>
                <div class="pull-left text-center">&nbsp;&nbsp;</div>
                <div class="heading right-label col-md-2"><label> Percent</label></div>
                <div class="pull-left text-center">&nbsp;&nbsp;&nbsp;&nbsp;</div>
                <div class="heading right-label col-md-2"><label> Amount</label></div>
                <div class="heading col-md-2 text-right"><label> Taxes</label></div>
              </div>

              <tax-line v-for="fee in formData.draftItem.fees"
                :key="fee.name"
                :model="fee"
                :compute="computeItemTotal"
                :details="openAddFeeTaxesDialog"
                :context="vm"
                class="row"
              />

              <div class="row bold-text small">
                <div class="col-xs-6 col-md-6 text-right">Subtotal:</div>
                <div class="pull-left text-center">&nbsp;&nbsp;</div>
                <div class="col-xs-2 col-md-2 text-right" name="fees_subtotal">
                  <v-money :currency="currency" :supplementalPrecision="6">{{ formData.draftItem.totalFees || 0 }}</v-money>
                </div>
                <div class="pull-left text-center">&nbsp;&nbsp;&nbsp;&nbsp;</div>
                <div class="col-xs-2 col-md-2 text-right" name="fee_taxes_subtotal">
                  <v-money :currency="currency" :supplementalPrecision="6">{{ formData.draftItem.totalTaxesForFees || 0}}</v-money>
                </div>
              </div>
            </section>
          </div>
        </section>

        <section class="col-md-12 no-pad">
          <div class="form-group">
            <div class="row">
              <hr />
              <div class="pull-left text-center">&nbsp;&nbsp;</div>
              <div class="col-xs-8 col-md-8 text-right bold-text">Subtotal with taxes and fees:</div>
              <div class="pull-left text-center">&nbsp;&nbsp;&nbsp;&nbsp;</div>
              <div class="col-xs-2 text-right" name="subtotal_with_fees_and_taxes">
                <v-money :currency="currency" class="bold-text">{{ formData.draftItem.totalWithTaxesAndFees || 0}}</v-money>
              </div>
            </div>
          </div>
        </section>

        <div class="clear"></div>
      </div>
    </ed-modal>
    <ed-modal
      :show='showVatGstPercentConfirmation'
      title='VAT/GST Percent Confirmation'
      primaryButton='SAVE ITEM'
      secondaryButton='BACK'
      @confirm='onAcceptVatGstPercentConfirmation'
      @close='showVatGstPercentConfirmation = false'
      :closeOnEsc="true"
    >
      <div v-if="model.vatGstPercentagesPerCountry !== null" class="content form-group" slot="body">
        <p>The VAT/GST percent is not valid in the country of the supplier. <br/> The valid percentages for {{ model.supplierCountry }} are:</p><br />
        <ul>
          <li v-for="percent in model.vatGstPercentagesPerCountry[this.model.supplierCountry]">
            {{ percent }}%
          </li>
        </ul>
        <br />
        <p>Please make sure the VAT/GST percent is correct before continuing as this will affect payments.</p>		  
      </div>
    </ed-modal>
  </div>
</template>

<script>
/* eslint spec-file: 0 */

import TaxLine from '../../components/taxLine'

import AddFeeTaxes from './AddFeeTaxes.vue'
import LineItemStore from './LineItemStore.js'
import customFields from '@/core/components/Form/customFields.vue'
import { EventBus } from '@/event-bus'
import Api from '../../api'

const api = new Api()

import Vue from 'vue'
var eventHub = new Vue()

export default {
  name: 'AddLineItem' /* mandatory field */,

  components: {
    TaxLine,
    customFields
  },

  props: {
    model: Object,
    eventHub: Object,
    dependencies: Object,
    eventId: String
  },

  created() {
    eventHub.$on('onCloseAddFeeTaxes', this.onCloseAddFeeTaxes)
    eventHub.$on('onAddedFeeTaxes', this.onAddedFeeTaxes)

    this.showVatGstPercentConfirmation = false
    this.showVatGstPercentConfirmationWasAccepted = false
    this.isEdit = false
    if (this.model.draftItem) {
      this.formData.draftItem = this.model.draftItem
      this.title = 'Edit Line Item'
      this.isEdit = true
      this.prefferedRateChanged = true
      this.negotiatedRateChanged = true
      this.unitPriceWasChanged = true
    } else {
      this.formData.draftItem = this.getNewLineItem()
    }

    if (this.model.useSavingsFields) {
      this.computePoTotalEndpoint = api.SavingsPurchaseOrderComputeTotals()
      this.computeItemTotalEndpoint = api.SavingsComputeLineItemTotals().replace('{ENV}', '')
    }

    var dialog = this
    this.store()
      .getLineItemLists(
        this.model.programId,
        this.model.invoiceId,
        this.isEdit
      )
      .then(response => {
        dialog.bindCostCenterAndBusinessUnit(response.data, this.isEdit)
        dialog.categories = response.data.categories
        dialog.formData.draftItem.selectedCategory = dialog.categories.filter(
          category => category.text === dialog.formData.draftItem.category
        )[0]

        dialog.model.units = response.data.itemUnits
        dialog.formData.draftItem.unitTypeModel = dialog.model.units.filter(
          unit => unit.key === dialog.formData.draftItem.unitType
        )[0]
        // select default unit
        if (!this.isEdit && response.data.defaultUnit) {
          this.unitsChanged(response.data.defaultUnit)
        }

        if (!this.isEdit) { // when supplier or category is changed
          this.getFeesAndTaxes(dialog.model.supplierVci, 0, dialog.model.currency.code)
        }
      })
  },

  mounted() {
    setTimeout(() => {
      this.$refs.productNameField.$refs.input.focus()
    })
  },

  beforeDestroy() {
    this.prefferedRateChanged = false
    this.negotiatedRateChanged = false
    this.unitPriceWasChanged = false
    eventHub.$off('onCloseAddFeeTaxes', this.onCloseAddFeeTaxes)
    eventHub.$off('onAddedFeeTaxes', this.onAddedFeeTaxes)
  },

  data() {
    return {
      vm: this,
      showDiscountsFeesTaxes: false,
      formData: {
        toggleAllEventsState: false,
        draftItem: {}
      },
      show: true,

      validation: true,
      computePoTotalEndpoint: api.PurchaseOrderComputeTotals(),
      computeItemTotalEndpoint: api.ComputeLineItemTotals().replace('{ENV}', ''),
      title: 'Add Line Item',
      categories: [],
      bussinessUnitsWithCostCenters: [],
      units: [],

      doDisplayNameError: false,
      doDisplayUnitPriceError: false,

      isCategorySelected: false,
      doDisplayCategoryError: false,
      doDisplayCategRefIdError: false,

      isBusinessUnitAndCostCenterSelected: false,
      doDisplayBusinessUnitAndCostCenterError: false,

      isUnitSelected: false,
      doDisplayUnitsError: false,
      doDisplayUnitsInputError: false,

      showOneCostCenter: false,
      showCostCenters: false,
      showOneBusinessUnit: false,
      showBusinessUnits: false,
      showOneCostCenterWithBusinessUnit: false,
      showMixCostCentersWithBusinessUnits: false,

      isCostCenterSelected: false,
      doDisplayCostCenterError: false,

      isBusinessUnitSelected: false,
      doDisplayBusinessUnitError: false,

      isMixCostCenterWithBusinessUnitSelected: false,
      doDisplayMixCostCenterWithBusinessUnitError: false,

      doDisplayQuantityError: false,
      isEdit: false,

      negotiatedRateWasChanged: false,
      prefferedRateWasChanged: false,
      unitPriceWasChanged: false,
      quantityErrorMsg: '',
      unitsErrorMsg: '',
      unitPriceErrorMsg: '',
      quantityEmptyErrorMsg: 'Please select a quantity',
      quantityThresholdErrorMsgTpl: 'Quantity must be less than or equal to {0}',
      unitsThresholdErrorMsgTpl: 'Units must be less than or equal to {0}',
      unitPriceThresholdErrorMsgTpl: 'Unit price must be less than or equal to {0}',
      unitPriceNonZeroMsg: 'Unit price must not be 0',

      showVatGstPercentConfirmation: false,
      showVatGstPercentConfirmationWasAccepted: false
    }
  },

  computed: {
    disableCategoryRefInput() {
      return !this.dependencies.canEditCategRefId || this.model.canEditOnlyPrice
    },
    currency() {
      return this.model.currency.code
    },
    isRequiredCategRefID() {
      return this.model.IsRequiredCategRefID
    },
    doDisplayCategRefIdErrorComputed() {
      return this.doDisplayCategRefIdError
    },
    supplementalPrecision() {
      return this.model.useSupplementalPrecision ? 6 /* 8 digits */ : 0 /* 2 digits */
    },
    hasSyncSuppliersToAccountingSystem() {
      return global.app.getUser.settings.SyncSuppliersToAccountingSystem
    }
  },

  methods: {
    getCostCenterName(option, label) {
      return option.name
              ? option.name.indexOf(option.code) === -1
                                                      ? option.code + ' (' + option.name + ')'
                                                      : option.name
              : option.code
    },
    getInternalProgramId(r) {
      return r.programDetails.uniqueId
    },

    getTaxId(name) {
      return 'item_tax_' + name
    },

    getNamePercent(name) {
      return name + '_percent'
    },

    getNameAmount(name) {
      return name + '_amount'
    },

    getNameLink(name) {
      return name + '_link'
    },

    getNewLineItem() {
      let item = {
        productName: '',
        description: '',
        internalNote: '',
        category: '',
        referenceId: '',
        pairedBusinessUnitAndCostCenter: '',
        costCenterAndBusinessUnit: {},
        unit: '',
        taxCode: '',
        unitPrice: '',
        quantity: '1',
        units: '',
        total: '',
        discountAmount: 0,
        discountPercent: '',
        subtotal: 0,
        subtotalTaxes: 0,
        totalTaxes: 0,
        listOrder: 1,
        taxes: JSON.parse(JSON.stringify(this.model.taxes)),
        fees: JSON.parse(JSON.stringify(this.model.fees)),
        totalWithTaxesAndFees: 0,
        selectedFee: {
          taxes: JSON.parse(JSON.stringify(this.model.taxes)) || []
        },
        draftFee: {
          taxes: JSON.parse(JSON.stringify(this.model.taxes))
        },
        displayFeesTaxesModal: false
      }

      if (this.model.useSavingsFields) {
        item.unitPrice = 0
        item.unitPriceSGR = 0
        item.unitPriceNegotiatedRate = 0
        item.unitPricePreferredRate = 0
        item.totalSGR = 0
        item.totalPreferredRate = 0
        item.totalNegotiatedRate = 0
      }

      return item
    },

    store() {
      return new LineItemStore(this.api)
    },

    onAcceptVatGstPercentConfirmation() {
      this.showVatGstPercentConfirmation = false
      this.showVatGstPercentConfirmationWasAccepted = true
      this.onSave()
    },

    costCenterSelected(e) {
      if (this.showCostCenters) {
        this.isCostCenterSelected = true
        this.doDisplayCostCenterError = false
        this.formData.draftItem.costCenterAndBusinessUnit = {
          programBusinessUnitAndCostCenterId: +e.key,
          costCenterNo: e.code,
          costCenterName: e.name,
          costCenterId: +e.costCenterId
        }
      }
    },

    businessUnitSelected(e) {
      if (this.showBusinessUnits) {
        this.isBusinessUnitSelected = true
        this.doDisplayBusinessUnitError = false
        this.formData.draftItem.costCenterAndBusinessUnit = {
          businessUnitName: e.name,
          businessUnitId: +e.businessUnitId
        }
      }
    },

    businessUnitWithCostCenterSelected(e) {
      if (this.showMixCostCentersWithBusinessUnits) {
        this.isMixCostCenterWithBusinessUnitSelected = true
        this.doDisplayMixCostCenterWithBusinessUnitError = false
        this.formData.draftItem.costCenterAndBusinessUnit = {
          costCenterAndBusinessUnitName: e.name
        }
      }
    },

    bindCostCenterAndBusinessUnit(data, isEdit) {
      var costCenters = data.costCentersAndBusinessUnit.costCenters
      var businessUnits = data.costCentersAndBusinessUnit.businessUnits
      var mixedCostCentersAndBusinessUnits =
        data.costCentersAndBusinessUnit.mixedCostCentersAndBusinessUnits

      if (
        mixedCostCentersAndBusinessUnits !== null &&
        mixedCostCentersAndBusinessUnits !== undefined &&
        mixedCostCentersAndBusinessUnits.length > 0
      ) {
        if (mixedCostCentersAndBusinessUnits.length === 1) {
          this.businessUnitsWithCostCenter =
            mixedCostCentersAndBusinessUnits[0]

          this.formData.mixedCostCentersAndBusinessUnits =
            mixedCostCentersAndBusinessUnits[0]

          this.showOneCostCenter = false
          this.showCostCenters = false
          this.showOneBusinessUnit = false
          this.showBusinessUnits = false
          this.showOneCostCenterWithBusinessUnit = true
          this.showMixCostCentersWithBusinessUnits = false
          this.formData.draftItem.pairedBusinessUnitAndCostCenter =
            mixedCostCentersAndBusinessUnits[0]

          if (!isEdit) {
            this.formData.draftItem.costCenterAndBusinessUnit.costCenterAndBusinessUnitKey =
              this.businessUnitsWithCostCenter.key
            this.formData.draftItem.costCenterAndBusinessUnit.costCenterAndBusinessUnitName =
              this.businessUnitsWithCostCenter.name
            this.formData.draftItem.costCenterAndBusinessUnit.businessUnitId =
              +this.businessUnitsWithCostCenter.businessUnitId
            this.formData.draftItem.costCenterAndBusinessUnit.costCenterId =
              +this.businessUnitsWithCostCenter.costCenterId
            this.formData.draftItem.costCenterAndBusinessUnit.programBusinessUnitAndCostCenterId =
              +this.businessUnitsWithCostCenter.programBusinessUnitAndCostCenterId
          } else {
            this.formData.draftItem.pairedBusinessUnitAndCostCenter = {
              key: this.formData.draftItem.costCenterAndBusinessUnit.costCenterAndBusinessUnitKey,
              name: this.formData.draftItem.costCenterAndBusinessUnit.costCenterAndBusinessUnitName,
              businessUnitId: +this.formData.draftItem.costCenterAndBusinessUnit.businessUnitId,
              costCenterId: +this.formData.draftItem.costCenterAndBusinessUnit.costCenterId,
              programBusinessUnitAndCostCenterId: +this.formData.draftItem.costCenterAndBusinessUnit.programBusinessUnitAndCostCenterId
            }
          }
        } else {
          this.businessUnitsWithCostCenters = mixedCostCentersAndBusinessUnits

          if (
            this.formData.draftItem.costCenterAndBusinessUnit !== undefined
          ) {
            this.formData.draftItem.pairedBusinessUnitAndCostCenter = this.businessUnitsWithCostCenters.filter(
              businessUnitWithCostCenter =>
                businessUnitWithCostCenter.key ===
                this.formData.draftItem.costCenterAndBusinessUnit
                  .costCenterAndBusinessUnitKey
            )
            if (this.formData.draftItem.pairedBusinessUnitAndCostCenter) {
              this.formData.draftItem.pairedBusinessUnitAndCostCenter =
                this.formData.draftItem.pairedBusinessUnitAndCostCenter[0]
              this.isBusinessUnitAndCostCenterSelected = true
            }
          }

          this.showOneCostCenter = false
          this.showCostCenters = false
          this.showOneBusinessUnit = false
          this.showBusinessUnits = false
          this.showOneCostCenterWithBusinessUnit = false
          this.showMixCostCentersWithBusinessUnits = true
        }
      } else {
        if (
          costCenters !== null &&
          costCenters !== undefined &&
          costCenters.length > 0
        ) {
          if (costCenters.length === 1) {
            this.costCenter = costCenters[0]
            this.formData.costCenter = costCenters[0]

            this.showOneCostCenter = true
            this.showCostCenters = false
            this.showOneBusinessUnit = false
            this.showBusinessUnits = false
            this.showOneCostCenterWithBusinessUnit = false
            this.showMixCostCentersWithBusinessUnits = false

            if (!isEdit) {
              this.formData.draftItem.costCenterAndBusinessUnit.programBusinessUnitAndCostCenterId =
                this.costCenter.key
              this.formData.draftItem.costCenterAndBusinessUnit.costCenterNo =
                this.costCenter.code
              this.formData.draftItem.costCenterAndBusinessUnit.costCenterName =
                this.costCenter.name
              this.formData.draftItem.costCenterAndBusinessUnit.costCenterId =
                this.costCenter.costCenterId
            } else {
              this.formData.draftItem.costCenter = {
                key: +this.formData.draftItem.costCenterAndBusinessUnit.programBusinessUnitAndCostCenterId,
                code: this.formData.draftItem.costCenterAndBusinessUnit.costCenterNo,
                name: this.formData.draftItem.costCenterAndBusinessUnit.costCenterName,
                costCenterId: +this.formData.draftItem.costCenterAndBusinessUnit.costCenterId
              }
            }
          } else {
            this.costCenters = costCenters
            this.showOneCostCenter = false
            this.showCostCenters = true
            this.showOneBusinessUnit = false
            this.showBusinessUnits = false
            this.showOneCostCenterWithBusinessUnit = false
            this.showMixCostCentersWithBusinessUnits = false

            if (
              this.formData.draftItem.costCenterAndBusinessUnit !==
              undefined
            ) {
              this.formData.draftItem.costCenter = costCenters.filter(
                costCenter =>
                  +costCenter.costCenterId ===
                    this.formData.draftItem.costCenterAndBusinessUnit.costCenterId
              )
              // array to object
              if (this.formData.draftItem.costCenter) {
                this.formData.draftItem.costCenter = this.formData.draftItem.costCenter[0]
              }
              if (this.formData.draftItem.costCenter &&
                this.formData.draftItem.costCenterAndBusinessUnit.costCenterNo) {
                this.isCostCenterSelected = true
              }
            }
          }
        }

        if (
          businessUnits !== null &&
          businessUnits !== undefined &&
          businessUnits.length > 0
        ) {
          if (businessUnits.length === 1) {
            this.businessUnit = businessUnits[0]
            this.formData.businessUnit = businessUnits[0]
            this.showOneBusinessUnit = true
            this.showBusinessUnits = false
            this.showOneCostCenterWithBusinessUnit = false
            this.showMixCostCentersWithBusinessUnits = false

            if (!isEdit) {
              this.formData.draftItem.costCenterAndBusinessUnit.businessUnitName =
                this.businessUnit.name
              this.formData.draftItem.costCenterAndBusinessUnit.businessUnitId =
                this.businessUnit.businessUnitId
            } else {
              this.formData.draftItem.businessUnit = {
                key: this.formData.draftItem.costCenterAndBusinessUnit.businessUnitName,
                name: this.formData.draftItem.costCenterAndBusinessUnit.businessUnitName,
                businessUnitId: +this.formData.draftItem.costCenterAndBusinessUnit.businessUnitId
              }
            }
          } else {
            this.businessUnits = businessUnits
            this.showOneBusinessUnit = false
            this.showBusinessUnits = true
            this.showOneCostCenterWithBusinessUnit = false
            this.showMixCostCentersWithBusinessUnits = false

            if (this.formData.draftItem.costCenterAndBusinessUnit !== undefined) {
              this.formData.draftItem.businessUnit = businessUnits.filter(
                bu => bu.key === this.formData.draftItem.costCenterAndBusinessUnit.businessUnitName
              )
              // array to object
              if (this.formData.draftItem.businessUnit) {
                this.formData.draftItem.businessUnit = this.formData.draftItem.businessUnit[0]
              }
              if (this.formData.draftItem.businessUnit &&
                this.formData.draftItem.costCenterAndBusinessUnit.businessUnitName) {
                this.isBusinessUnitSelected = true
              }
            }
          }
        }
      }
    },

    openAddFeeTaxesDialog(fee) {
      this.formData.draftItem.selectedFee = fee
      this.formData.draftItem.draftFee = JSON.parse(JSON.stringify(fee))
      this.formData.draftItem.draftFee.currency = this.model.currency

      var ComponentClass = Vue.extend(AddFeeTaxes)
      var instance = new ComponentClass({
        propsData: {
          type: 'primary',
          model: this.formData.draftItem.draftFee,
          dependencies: this.dependencies,
          eventHub: eventHub
        }
      })
      instance.$slots.default = []
      instance.$mount()
      this.$refs.addFeeTaxesDialogContainer.appendChild(instance.$el)
    },

    onCloseAddFeeTaxes() {},

    onAddedFeeTaxes(e) {
      if (this.formData.draftItem.selectedFee) {
        this.formData.draftItem.selectedFee.taxes = e.eventData.taxes
      }
      this.computeItemTotal()
    },

    onClose() {
      EventBus.$emit('onCloseAddLineItem', {})
      this.show = false
    },

    onSave() {
      if (!this.isValid()) return
      if (!this.showVatGstPercentConfirmationWasAccepted && this.model.supplierCountry && this.model.vatGstPercentagesPerCountry && this.model.vatGstPercentagesPerCountry[this.model.supplierCountry]) {
        let itemIsValid = true
        this.formData.draftItem.taxes.forEach((tax) => {
          if (tax.isChecked && (tax.name === 'Value Added Tax (VAT)' || tax.name === 'Goods & Services Tax (GST)')) {
            let taxPercent = tax.percentage
            if (!taxPercent) {
              taxPercent = (parseFloat(tax.value) * 100) / this.formData.draftItem.subtotal
            }
            let percentages = this.model.vatGstPercentagesPerCountry[this.model.supplierCountry]
            let isValid = false
            for (let i = 0; i < percentages.length; i++) {
              let noOfDecimals = 0
              if (percentages[i].indexOf('.') >= 0) {
                noOfDecimals = percentages[i].split('.')[1].length
              }
              if (percentages[i] === parseFloat(taxPercent).toFixed(noOfDecimals)) {
                isValid = true
              }
            }
            if (!isValid) {
              this.showVatGstPercentConfirmation = true
              itemIsValid = false
            }
          }
        })
        if (!itemIsValid) return
      }
      this.formData.draftItem.isFeeDiscountItem = false
      this.formData.draftItem.unitType = this.formData.draftItem.unitTypeModel.key
      if (typeof this.formData.draftItem.costCenterAndBusinessUnit === 'undefined') {
        this.formData.draftItem.costCenterAndBusinessUnit = {}
      }
      if (this.showOneCostCenter) {
        this.formData.draftItem.costCenterAndBusinessUnit.programBusinessUnitAndCostCenterId = +this.costCenter.key
        this.formData.draftItem.costCenterAndBusinessUnit.costCenterNo = this.costCenter.code
        this.formData.draftItem.costCenterAndBusinessUnit.costCenterName = this.costCenter.name
        this.formData.draftItem.costCenterAndBusinessUnit.costCenterId = +this.costCenter.costCenterId
      } else if (this.formData.draftItem.costCenter) {
        this.formData.draftItem.costCenterAndBusinessUnit.programBusinessUnitAndCostCenterId = +this.formData.draftItem.costCenter.key
        this.formData.draftItem.costCenterAndBusinessUnit.costCenterNo = this.formData.draftItem.costCenter.code
        this.formData.draftItem.costCenterAndBusinessUnit.costCenterName = this.formData.draftItem.costCenter.name
        this.formData.draftItem.costCenterAndBusinessUnit.costCenterId = +this.formData.draftItem.costCenter.costCenterId
      }

      if (this.showOneBusinessUnit) {
        this.formData.draftItem.costCenterAndBusinessUnit.businessUnitName = this.businessUnit.name
        this.formData.draftItem.costCenterAndBusinessUnit.businessUnitId = +this.businessUnit.businessUnitId
      } else if (this.formData.draftItem.businessUnit) {
        this.formData.draftItem.costCenterAndBusinessUnit.businessUnitName = this.formData.draftItem.businessUnit.name
        this.formData.draftItem.costCenterAndBusinessUnit.businessUnitId = +this.formData.draftItem.businessUnit.businessUnitId
      }

      if (this.showOneCostCenterWithBusinessUnit) {
        this.formData.draftItem.costCenterAndBusinessUnit.costCenterAndBusinessUnitKey =
          this.businessUnitsWithCostCenter.key
        this.formData.draftItem.costCenterAndBusinessUnit.costCenterAndBusinessUnitName =
          this.businessUnitsWithCostCenter.name
        this.formData.draftItem.costCenterAndBusinessUnit.businessUnitId =
          +this.businessUnitsWithCostCenter.businessUnitId
        this.formData.draftItem.costCenterAndBusinessUnit.costCenterId =
          +this.businessUnitsWithCostCenter.costCenterId
        this.formData.draftItem.costCenterAndBusinessUnit.programBusinessUnitAndCostCenterId =
          +this.businessUnitsWithCostCenter.programBusinessUnitAndCostCenterId
        this.formData.draftItem.useBusinessUnitAndCostCenterMapping = true
      } else if (this.formData.draftItem.pairedBusinessUnitAndCostCenter) {
        this.formData.draftItem.costCenterAndBusinessUnit.costCenterAndBusinessUnitKey =
          this.formData.draftItem.pairedBusinessUnitAndCostCenter.key
        this.formData.draftItem.costCenterAndBusinessUnit.costCenterAndBusinessUnitName =
          this.formData.draftItem.pairedBusinessUnitAndCostCenter.name
        this.formData.draftItem.costCenterAndBusinessUnit.businessUnitId =
          +this.formData.draftItem.pairedBusinessUnitAndCostCenter.businessUnitId
        this.formData.draftItem.costCenterAndBusinessUnit.costCenterId =
          +this.formData.draftItem.pairedBusinessUnitAndCostCenter.costCenterId
        this.formData.draftItem.costCenterAndBusinessUnit.programBusinessUnitAndCostCenterId =
          +this.formData.draftItem.pairedBusinessUnitAndCostCenter.programBusinessUnitAndCostCenterId
        this.formData.draftItem.useBusinessUnitAndCostCenterMapping = true
      } else {
        this.formData.draftItem.useBusinessUnitAndCostCenterMapping = false
      }

      this.formData.draftItem.customFields = this.$refs.itemCustomFieldsRef.getFields()

      EventBus.$emit('onSaveLineItem', {
        data: this.formData.draftItem,
        eventUniqueId: this.eventId
      })
      this.$nextTick(() => {
        this.show = false
      })
    },

    sgrChanged(value) {
      if (!this.isEdit && !this.prefferedRateWasChanged) {
        this.formData.draftItem.unitPricePreferredRate = value

        if (!this.negotiatedRateWasChanged) {
          this.formData.draftItem.unitPriceNegotiatedRate = value

          if (!this.unitPriceWasChanged) {
            this.formData.draftItem.unitPrice = value
          }
        }
      }

      this.computeItemTotal()
    },

    prefferedRateChanged(value) {
      this.prefferedRateWasChanged = true

      if (!this.isEdit && !this.negotiatedRateWasChanged) {
        this.formData.draftItem.unitPriceNegotiatedRate = value

        if (!this.unitPriceWasChanged) {
          this.formData.draftItem.unitPrice = value
        }
      }

      this.computeItemTotal()
    },

    negotiatedRateChanged(value) {
      this.negotiatedRateWasChanged = true

      if (!this.isEdit && !this.unitPriceWasChanged) {
        this.formData.draftItem.unitPrice = value
      }

      this.computeItemTotal()
    },

    unitPriceChanged(value) {
      this.unitPriceWasChanged = true
      this.doDisplayUnitPriceError = false

      this.computeItemTotal()
    },

    computeItemTotal() {
      return new Promise((resolve, reject) => {
        this.getComputeItemTotalRequest()
          .then(response => {
            this.formData.draftItem = this.store()
              .populateTotalsOnItem(
                this.formData.draftItem,
                response.data.lineItem
              )

            resolve(response)
          })
          .catch(error => reject(error))
      })
    },

    getComputeItemTotalRequest() {
      if (this.model.useSavingsFields) {
        return this.store()
          .computeSavingsItemTotal(this.formData.draftItem, this.model.currency)
      } else {
        return this.store()
          .computeItemTotal(this.formData.draftItem, this.model.currency)
      }
    },

    categoryChanged(category) {
      this.formData.draftItem.category = category.key
      this.formData.draftItem.selectedCategory = category
      this.formData.draftItem.referenceId = category.referenceId
      this.isCategorySelected = true
      this.doDisplayCategoryError = false

      if (this.isCategRefIdFilled()) {
        this.doDisplayCategRefIdError = false
      }

      if (!this.isEdit) {
        let categoryId = this.getCategoryId(category)
        this.getFeesAndTaxes(this.model.supplierVci, categoryId, this.model.currency.code)
      }
    },

    getCategoryId(category) {
      let categoryId = category.key

      // if category.key contains also subcategoryId, select just the categoryId
      let idx = category.key.indexOf('_')
      if (idx && idx > 0) {
        let subcategoryId = category.key.substring(0, idx)
        if (subcategoryId) {
          categoryId = subcategoryId
        }
      }

      return categoryId
    },

    unitsChanged(unitType) {
      this.isUnitSelected = true
      this.doDisplayUnitsError = false
      this.formData.draftItem.unitTypeModel = unitType
      this.formData.draftItem.unitType = unitType.key
    },

    quantityChanged(quantity) {
      this.doDisplayQuantityError = false
      this.updateFeesPerItem(quantity)
      this.isQuantityThresholdValid()
    },

    unitsInputChanged() {
      this.doDisplayUnitsInputError = false

      this.isUnitsThresholdValid()
    },

    unitPriceInputChanged() {
      this.doDisplayUnitPriceError = false

      this.isUnitPriceThresholdValid()
    },

    updateFeesPerItem(qty) {
      this.formData.draftItem.fees &&
      this.formData.draftItem.fees.map(fee => {
        if (parseInt(fee.valuePerItem) !== 0) {
          fee.value = qty * fee.valuePerItem
        }
      })
    },

    itemNameChanged(name) {
      this.doDisplayNameError = false
    },

    categoryRefIdChanged(referenceId) {
      this.doDisplayCategRefIdError = false
      this.formData.draftItem.referenceId = referenceId
      this.formData.draftItem.selectedCategory.referenceId = referenceId
    },

    getFeesAndTaxes(supplierVCI, categoryId, currencyCode) {
      this.getTaxes(supplierVCI, categoryId, currencyCode)
      this.getFees(supplierVCI, categoryId, currencyCode)
    },

    getTaxes(supplierVCI, categoryId, currencyCode) {
      this.store()
        .getLineItemTaxes(
          this.model.supplierVci || 0,
          categoryId || 0,
          this.model.currency.code
        )
        .then(response => {
          this.formData.draftItem.taxes = response.data
          this.computeItemTotal()
        })
    },

    getFees(supplierVCI, categoryId, currencyCode) {
      this.store()
        .getLineItemFees(
          this.model.supplierVci || 0,
          categoryId || 0,
          this.model.currency.code,
          this.model.events,
          this.model.buyerVendorId
        )
        .then(response => {
          this.formData.draftItem.fees = response.data
          if (this.isQuantitySelected()) {
            this.updateFeesPerItem(this.formData.draftItem.quantity)
          }
          this.computeItemTotal()
        })
    },

    isQuantitySelected() {
      return this.formData.draftItem &&
        this.formData.draftItem.quantity &&
        this.formData.draftItem.quantity.toString().length &&
        this.formData.draftItem.quantity.toString().length > 0
    },

    isNameSelected() {
      return this.formData.draftItem &&
        this.formData.draftItem.name &&
        this.formData.draftItem.name.trim().length &&
        this.formData.draftItem.name.trim().length > 0
    },

    isCategRefIdFilled() {
      return this.formData.draftItem &&
        this.formData.draftItem.referenceId &&
        this.formData.draftItem.referenceId.length &&
        this.formData.draftItem.referenceId.length > 0
    },

    isQuantityThresholdValid() {
      if (this.model.canEditOnlyPrice && this.model.checkQuantitiesAgainstPO && this.model.maxThreshold) {
        if (Number(this.formData.draftItem.quantity) > this.model.maxThreshold.quantity) {
          this.doDisplayQuantityError = true
          this.quantityErrorMsg = this.quantityThresholdErrorMsgTpl.replace('{0}', Number(this.model.maxThreshold.quantity))
          return false
        }
      }
      return true
    },

    isUnitsThresholdValid() {
      if (this.model.canEditOnlyPrice && this.model.checkQuantitiesAgainstPO && this.model.maxThreshold) {
        if (Number(this.formData.draftItem.units) > this.model.maxThreshold.units) {
          this.doDisplayUnitsInputError = true
          this.unitsErrorMsg = this.unitsThresholdErrorMsgTpl.replace('{0}', Number(this.model.maxThreshold.units))
          return false
        }
      }
      return true
    },

    isUnitPriceThresholdValid() {
      if (this.model.canEditOnlyPrice && this.model.maxThreshold) {
        if (Number(this.formData.draftItem.unitPrice) > this.model.maxThreshold.unitPrice) {
          this.doDisplayUnitPriceError = true
          this.unitPriceErrorMsg = this.unitPriceThresholdErrorMsgTpl.replace('{0}', Number(this.model.maxThreshold.unitPrice))
          return false
        }
      }
      return true
    },

    isUnitPriceNonZero() {
      if (!+this.formData.draftItem.unitPrice) {
        this.doDisplayUnitPriceError = true
        return false
      }
      return true
    },

    isValid() {
      var result = true

      if (!this.isNameSelected()) {
        this.doDisplayNameError = true
        result = false
      } else {
        this.doDisplayNameError = false
      }
      if (!this.isQuantitySelected()) {
        this.doDisplayQuantityError = true
        this.quantityErrorMsg = this.quantityEmptyErrorMsg

        result = false
      }
      if (this.model.canEditOnlyPrice && this.model.maxThreshold) {
        if (this.model.checkQuantitiesAgainstPO && (!this.isQuantityThresholdValid() ||
          !this.isUnitsThresholdValid() || !this.isUnitPriceThresholdValid())) {
          result = false
        } else if (!this.model.checkQuantitiesAgainstPO && !this.isUnitPriceThresholdValid()) {
          result = false
        }
      }

      if (!this.isUnitPriceNonZero()) {
        result = false
      }

      if (
        (
          this.categories !== null &&
          this.categories.length !== null &&
          this.categories.length > 0
        )
      ) {
        if (!this.isCategorySelected &&
          !(this.formData.draftItem &&
          this.formData.draftItem.selectedCategory && this.formData.draftItem.selectedCategory !== null)) {
          this.doDisplayCategoryError = true
          result = false
        }
      }

      if (this.model.IsRequiredCategRefID && !this.isCategRefIdFilled()) {
        this.doDisplayCategRefIdError = true
        result = false
      } else {
        this.doDisplayCategRefIdError = false
      }

      if (this.isUnitSelected === false &&
        !(this.formData.draftItem &&
          this.formData.draftItem.unitTypeModel && this.formData.draftItem.unitTypeModel !== null)) {
        this.doDisplayUnitsError = true
        result = false
      }

      if (this.showCostCenters) {
        if (!this.isCostCenterSelected &&
        !(this.formData.draftItem &&
          this.formData.draftItem.costCenter && this.formData.draftItem.costCenter !== null &&
          this.formData.draftItem.costCenter.key)) {
          this.doDisplayCostCenterError = true
          result = false
        }
      }

      if (this.showBusinessUnits) {
        if (!this.isBusinessUnitSelected &&
        !(this.formData.draftItem &&
          this.formData.draftItem.businessUnit && this.formData.draftItem.businessUnit !== null)) {
          this.doDisplayBusinessUnitError = true
          result = false
        }
      }

      if (this.showMixCostCentersWithBusinessUnits) {
        if (!this.isMixCostCenterWithBusinessUnitSelected &&
        !(this.formData.draftItem &&
          this.formData.draftItem.pairedBusinessUnitAndCostCenter && this.formData.draftItem.pairedBusinessUnitAndCostCenter !== null)) {
          this.doDisplayMixCostCenterWithBusinessUnitError = true
          result = false
        }
      }

      if (!this.$refs.itemCustomFieldsRef.validate()) {
        result = false
      }

      return result
    }
  }
}
</script>

<style scoped lang='scss'>
@import "~@/assets/sass/_variables.scss";

.form-group {
  section.pad {
    padding-left: 15px;
  }

  >>> .form-control {
    &.form-control {
      font-size: $font-size-base-sm;
    }
  }
}

.no-pad {
  padding: 0;
  margin: 0;
}

.heading {
  &.right-label {
    text-align: right;
    padding-right: 27px;
  }
  label {
    font-weight: $font-weight-extra-bold;
  }
}

.bold-text {
  font-weight: $font-weight-bold;
}

b {
  line-height: 2em;
}

.section-title {
  @include soft-font;
  font-size: $font-size-base-lg;
  font-weight: $font-weight-normal;
  @include theme {
    color: v(border);
  }
  margin-bottom: 7px;

  .icon {
    @include theme {
      color: v(ink);
    }
  }
}

</style>



// WEBPACK FOOTER //
// src/components/_modules/I2P/dialogs/LineItem/AddLineItem.vue